import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { errorBase } from 'utils/ui'

import i18n from 'i18next'
import { Spin } from 'antd'
import { getTempEnvParam } from 'env'
import ResourceService from 'api/Resource'
import RecordDetail from 'pages/records/components/RecordDetail'
import RecordEdit from 'pages/records/components/RecordEdit'
import RecordSharing from 'pages/records/components/RecordSharing'
import { shouldDecryptItem } from './decryptGuard'

// 에러 코드에 따른 에러 메시지 매핑
const ERROR_MESSAGES = {
  430: 'workspace.error.430',
  default: 'workspace.error.null',
}

// 에러 처리 함수
const handleRecordError = (code, message) => {
  console.log(' --- record error ---', message)

  // 431은 expired 상태로 별도 처리되므로 에러 표시 안함
  if (code !== 431 && code !== 870) {
    errorBase({
      title: i18n.t('common.title.warning'),
      content: i18n.t(ERROR_MESSAGES[code] || ERROR_MESSAGES.default),
    })
  }
}

// 응답 처리 함수
const processResourceResponse = (response) => {
  const { data, code, message, headerAccessLevel } = response

  if (code === 200 && data?.resource_key) {
    return {
      success: true,
      code: 200,
      data,
      headerAccessLevel,
    }
  }

  if (code === 431) {
    handleRecordError(code, message)
    return {
      success: false,
      code: 431,
      expiredData: data,
    }
  }

  if (code === 430) {
    handleRecordError(code, message)
    return {
      success: false,
      code: 430,
      data: null,
    }
  }

  handleRecordError(code, message)
  return {
    success: false,
    code,
    data: null,
  }
}

export const fetchIndividualRecord = async (resourceId, locale, callback, isZDW = false) => {
  // 기본 반환 객체
  const defaultResult = {
    success: false,
    data: null,
    code: 0,
  }

  // resourceId가 없으면 바로 반환
  if (!resourceId) {
    await callback(defaultResult)
    return
  }

  try {
    const response = await ResourceService.get({
      id: resourceId,
      locale,
      isZDW,
    })

    const result = processResourceResponse(response)

    console.log('fetchIndividualRecord result:', result)
    await callback(result)
  } catch (error) {
    console.log(error)
    await callback({
      success: false,
      code: -1,
      data: null,
    })
  }
}

export const getRsKeyObjFromResource = async (data) => {
  let rsaEncDec = getTempEnvParam('rsaObj')

  if (data != null && data.group_key != null) {
    const { user_group_key: encGroupKey, group_private_key: encPrivateKey } = data.group_key
    const groupInfo = {
      id: data.id,
      enc_group_key: encGroupKey,
      enc_private_key: encPrivateKey,
      name: data.title,
    }
    rsaEncDec = await rsaEncDec.getGroupObj(groupInfo)
  }

  let rsKeyObj = null

  if (data != null && data.resource_key != null) {
    rsKeyObj = rsaEncDec.createRSKeyObj(data.resource_key.key)
  } else {
    rsKeyObj = rsaEncDec.createRSKeyObj()
  }

  return rsKeyObj
}

export const getData = async (individual) => {
  const data = {
    ...individual,
  }

  const rsKeyObj = await getRsKeyObjFromResource(data)

  try {
    if (rsKeyObj != null && data != null && data.items != null) {
      await Promise.all(
        data.items.map(async (item) => {
          if (shouldDecryptItem(item)) {
            item.content = await rsKeyObj.decrypt(item.content)
          }
          return item
        }),
      )
    }
  } catch (error) {
    console.error(error)
  }

  return data
}

const RecordWrapper = (props) => {
  const {
    type,
    id,
    updatedAt,
    orgChartData,
    pageInfo,
    record,
    // handleViewAllDetails
  } = props
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [individual, setIndividual] = useState(null)
  const locale = useSelector((state) => state.settings.locale)

  // const totalCount = (record?.shared_groups?.length ?? 0) + (record?.shared_users?.length ?? 0)
  const tablePage = getTempEnvParam('curTablePage')

  const loadData = (resId, callbackFunc) => {
    setLoading(true)

    fetchIndividualRecord(resId, locale, async (retObj) => {
      const { data, code, expiredData, headerAccessLevel } = retObj
      if (data != null) {
        const decryptedData = await getData(data)
        setIndividual({ ...decryptedData, headerAccessLevel })
      } else if (code === 431) {
        setIndividual({ expired: true, expiredData })
      } else {
        tablePage.openItem(null)
      }
      if (callbackFunc != null) {
        callbackFunc()
      }
      setLoading(false)
    })
  }

  const reloadRecord = (callbackFunc) => {
    loadData(id, callbackFunc)
  }

  useEffect(() => {
    if (id != null) {
      loadData(id)
    } else {
      setIndividual(null)
    }

    // eslint-disable-next-line
  }, [id, updatedAt, type])

  const attrs = {
    ...props,
    orgChartData,
    individual,
    reloadRecord,
    setProcessing,
    getRsKeyObjFromResource,
    loading,
    setLoading,
    pageInfo,
    isLeaked: record?.isLeaked,
    // handleViewAllDetails,
  }

  let Comp = null
  switch (type) {
    case 'detail':
      Comp = RecordDetail
      break
    case 'create':
    case 'edit':
      Comp = RecordEdit
      break
    case 'share':
      Comp = RecordSharing
      break
    default:
      Comp = null
  }

  const spinning = loading === true || processing === true

  return <Spin spinning={spinning}>{Comp && <Comp {...attrs} />}</Spin>
}

export default RecordWrapper
